import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Flex } from '@adobe/react-spectrum';
import './Privacy.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { resurfaceConsent } from '../../utils/consent';
import { FEATURE_FLAGS } from '../../config/featureFlags';

/**
 * Privacy Policy page. Required by Google in order to serve ads via NitroPay,
 * and the destination for the CCPA "Do Not Sell or Share" + GDPR consent links.
 *
 * NOTE: this copy is a starting-point template, NOT legal advice. Ben should
 * review the entity name, governing-law jurisdiction, and data-partner list
 * before this goes live in production.
 */

const LAST_UPDATED = 'May 27, 2026';
const CONTACT_EMAIL = 'questions@commandersalt.com';

export function Privacy() {
    const helmetName = 'Privacy Policy';
    const helmetCanonical = 'https://www.commandersalt.com/privacy';
    const helmetDescription =
        'Commandersalt.com privacy policy, including advertising, cookies, and your privacy choices.';
    const helmetTitle = 'Commandersalt.com - Privacy Policy';

    // The footer links deep-link into specific sections (#ccpa, #consent).
    // React Router doesn't scroll to hash anchors on its own, so do it here —
    // otherwise every link just lands at the top and they look identical.
    const location = useLocation();
    useEffect(() => {
        if (!location.hash) {
            return;
        }
        const el = document.getElementById(location.hash.slice(1));
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [location]);

    // Reopen NitroPay's CMP if it's loaded. Until the ad script is installed
    // there's no CMP, so fall back to a pre-filled email request — that way the
    // control always does something and matches the opt-out path promised in the
    // copy ("email us at ...").
    const handlePrivacyChoice = (kind) => {
        if (resurfaceConsent()) {
            return;
        }
        const subject =
            kind === 'ccpa' ? 'Do Not Sell or Share My Personal Information' : 'Cookie & consent preferences request';
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
    };

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />

            <div className="privacyRoot">
                <section className="privacyHero">
                    <span className="privacyHeroEyebrow">Legal</span>
                    <h1 className="privacyHeroTitle">Privacy Policy</h1>
                    <p className="privacyHeroSubtitle">
                        How Commandersalt.com collects, uses, and shares information — including what our{' '}
                        {FEATURE_FLAGS.ads ? 'advertising and analytics' : 'analytics'} partners do, and the choices you
                        have over your data.
                    </p>
                    <span className="privacyUpdated">Last updated: {LAST_UPDATED}</span>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">Who we are</h2>
                    <p className="privacyCardBody">
                        Commandersalt.com (“the site”, “we”, “us”) is a hobby project for analyzing Magic: The Gathering
                        Commander/EDH decks. You can reach us at{' '}
                        <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                            {CONTACT_EMAIL}
                        </a>{' '}
                        with any privacy questions or requests.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">What we collect</h2>
                    <p className="privacyCardBody">
                        We collect a limited set of information to run and improve the site:
                    </p>
                    <ul className="privacyList">
                        <li>
                            <strong>Account information</strong> — if you sign in, the email and identifier provided
                            through our authentication provider (AWS Cognito).
                        </li>
                        <li>
                            <strong>Content you submit</strong> — decklists, deck URLs, and related data you choose to
                            import or save.
                        </li>
                        <li>
                            <strong>Usage &amp; device data</strong> — pages viewed, interactions, approximate location,
                            browser/device type, and similar analytics data, collected via cookies and similar
                            technologies.
                        </li>
                    </ul>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">Analytics</h2>
                    <p className="privacyCardBody">
                        We use Google Analytics and Firebase to understand how the site is used so we can improve it.
                        These tools set cookies and collect usage data as described in{' '}
                        <a
                            className="privacyLink"
                            href="https://policies.google.com/privacy"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Google’s Privacy Policy
                        </a>
                        .
                    </p>
                </section>

                {FEATURE_FLAGS.ads && (
                    <section className="privacyCard">
                        <h2 className="privacyCardTitle">Advertising</h2>
                        <p className="privacyCardBody">
                            We work with NitroPay to display advertising on the site. NitroPay and its advertising
                            partners — including Google and other ad exchanges — may use cookies, device identifiers,
                            and similar technologies to serve and measure ads, including personalized (interest-based)
                            ads.
                        </p>
                        <p className="privacyCardBody">
                            Third-party vendors, including Google, use cookies to serve ads based on your prior visits
                            to this and other websites. You can opt out of personalized advertising by Google by
                            visiting{' '}
                            <a
                                className="privacyLink"
                                href="https://adssettings.google.com"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Google Ads Settings
                            </a>
                            , and from other participating vendors at{' '}
                            <a
                                className="privacyLink"
                                href="https://optout.aboutads.info"
                                target="_blank"
                                rel="noreferrer"
                            >
                                aboutads.info
                            </a>{' '}
                            and{' '}
                            <a
                                className="privacyLink"
                                href="https://www.youronlinechoices.eu"
                                target="_blank"
                                rel="noreferrer"
                            >
                                youronlinechoices.eu
                            </a>
                            . NitroPay’s privacy practices are described in the{' '}
                            <a
                                className="privacyLink"
                                href="https://nitropay.com/privacy"
                                target="_blank"
                                rel="noreferrer"
                            >
                                NitroPay Privacy Policy
                            </a>
                            .
                        </p>
                    </section>
                )}

                {FEATURE_FLAGS.ads && (
                    <section className="privacyCard" id="consent">
                        <h2 className="privacyCardTitle">Your privacy choices &amp; consent (GDPR / UK)</h2>
                        <p className="privacyCardBody">
                            If you are in the European Economic Area, the United Kingdom, or another region requiring
                            consent, we ask for your consent before storing or accessing non-essential cookies or
                            processing personal data for personalized advertising. You can review or change those
                            choices at any time.
                        </p>
                        <p className="privacyCardBody">
                            You have the right to access, correct, delete, or restrict processing of your personal data,
                            and to withdraw consent. To exercise these rights, contact us at{' '}
                            <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                                {CONTACT_EMAIL}
                            </a>
                            .
                        </p>
                        <button
                            type="button"
                            className="privacyChoiceButton"
                            onClick={() => handlePrivacyChoice('consent')}
                        >
                            Manage Cookie &amp; Consent Settings
                        </button>
                    </section>
                )}

                {FEATURE_FLAGS.ads && (
                    <section className="privacyCard" id="ccpa">
                        <h2 className="privacyCardTitle">California privacy rights (CCPA / CPRA)</h2>
                        <p className="privacyCardBody">
                            If you are a California resident, you have the right to know what personal information we
                            collect, to request deletion, and to opt out of the “sale” or “sharing” of your personal
                            information. Sharing data with advertising partners for personalized ads may be considered a
                            “sale” or “share” under California law.
                        </p>
                        <p className="privacyCardBody">
                            To opt out, email us at{' '}
                            <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                                {CONTACT_EMAIL}
                            </a>
                            , or use the control below. We will not discriminate against you for exercising these
                            rights.
                        </p>
                        <button
                            type="button"
                            className="privacyChoiceButton"
                            onClick={() => handlePrivacyChoice('ccpa')}
                        >
                            Do Not Sell or Share My Personal Information
                        </button>
                    </section>
                )}

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">Cookies</h2>
                    <p className="privacyCardBody">
                        We and our partners use cookies and similar technologies for essential site functionality
                        {FEATURE_FLAGS.ads ? ', analytics, and advertising' : ' and analytics'}. You can control cookies
                        through your browser settings
                        {FEATURE_FLAGS.ads ? ' and through the consent controls above' : ''}. Disabling some cookies may
                        affect how the site works.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">Children’s privacy</h2>
                    <p className="privacyCardBody">
                        The site is not directed to children under 13 (or the applicable age in your jurisdiction), and
                        we do not knowingly collect personal information from them.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">Changes to this policy</h2>
                    <p className="privacyCardBody">
                        We may update this policy from time to time. Material changes will be reflected by updating the
                        “Last updated” date above.
                    </p>
                </section>
            </div>
        </Flex>
    );
}
